/* eslint-disable no-param-reassign */
/*
 ** 外设租借结算 抽屉
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { Form, Input, Button, Spin, message, Radio } from 'antd';
import intl from 'react-intl-universal';
import { orderChangePrice, orderBillNotPay, orderBillPay, preOrder } from '@/api/peripheral';
import { getOrderInfos, updateState, changeRentCount } from '@/redux/actions/peripheral';
import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';

// 支付相关
import Payment from '@/components/Payment';
import PayModal from '@/components/Payment/payModal';
import { PayModalRef, PayWay } from '@/components/Payment/interface';
import IntlContext from '@/components/Intl/IntlContext';
import { parseFen2Currency, Calculator } from '@/utils/number';

// 内部组件
import RentList from './components/RentList';
import TotalInfo from './components/TotalInfo';

import styles from './index.module.scss';

const { TextArea } = Input;

/**
 * @param IConfig 参数
 * @example portal.showDrawer({ orderNo, homeowner, callback });
 */
interface IConfig {
  orderNo: any;
  leaseType: 1 | 2; // 1-外设租借 2-外设续租
  homeowner: string; // 查房的主键id
  callback?: () => void; // 确认按钮的回调函数  此处最好是查房列表的数据重新获取
}

interface IProps extends IConfig {
  unmountNode: () => void;
}

const Index: React.FC<IConfig> = ($$: any) => {
  const [modalState, setModalState] = useState({ isInit: false, isLoading: false });

  const payModalRef = useRef<PayModalRef>();

  const [loading, setLoading] = useState<boolean>(false);

  // paymentTypes: 1, // 支付类型 0-入账并直接支付（默认） 1-先入帐，稍后统一结账
  const [paymentTypes, setPaymentTypes] = useState(0);

  // 商品总额信息
  const goodTotalInfo = {
    settleTotalActualPrice: $$.settleTotalActualPrice,
    settleTotalCount: $$.settleTotalCount,
    settleTotalTimeCount: $$.settleTotalTimeCount,
    actualPay: $$.actualPay,
  };

  // 订单号 & 房主信息
  const { orderNo, leaseType } = $$;
  const homeowner: any = JSON.parse($$.homeowner || '{}');

  // Payment & PayModal props
  const [payType, setPayType] = useState(3); // 支付方式(3:扫码支付, 2: 会员卡扣, 1:现金)
  const [certNo, setCertNo] = useState(''); // 身份证号(支持会员卡支付时必传)
  const [wyuid, setWyuid] = useState('');

  const updateModalState = useCallback<(state: Partial<typeof modalState>) => void>((state) => {
    setModalState((prevState) => ({ ...prevState, ...state }));
  }, []);

  // mounted
  useEffect(() => {
    updateModalState({ isInit: true });

    $$.getOrderInfos(orderNo);
  }, []);

  // 关闭结算抽屉
  const handleClose = () => {
    $$.unmountNode();
    if (typeof $$.callback === 'function') {
      $$.callback();
    }
  };

  // 不收款入账
  const noPayIntoOrder = async () => {
    setLoading(true);
    try {
      const res: any = await orderBillNotPay({
        orderNo,
        remark: $$.financialNotes || '',
        trigger: '挂账房间方式，且入账成功时',
        operationPage: '外设租借结算抽屉',
      });
      setLoading(false);
      if (res.code === 0) {
        message.success('外设入账成功');
        handleClose();
      }
    } catch (error) {
      setLoading(false);
    }
  };

  // payType changed callback
  const selectPayType = (type) => {
    setPayType(type);
  };

  // pay result callback
  const payResultCallback = (res: {
    closePayOrderModal: boolean;
    payed: boolean;
    result: boolean;
  }) => {
    // pay result, do something
    const { closePayOrderModal, payed, result } = res || {};

    // 关闭时跳转到商品选购页
    if (closePayOrderModal) {
      handleClose();
      return;
    }

    if (!payed && !result) {
      // just close, do nothing
      return;
    }

    // 没有支付成功 或者入账购买失败
    if ((!payed || !result) && $$.buyWay.saleType === 1) message.error('外设租借失败');
    // 没有支付成功 或者入账购买失败
    if ((!payed || !result) && $$.buyWay.saleType !== 1) message.error('外设入账失败');

    // 支付成功
    if (payed && result) {
      // 前台直售
      if ($$.buyWay.saleType === 1) message.success('外设租借成功');
      // 房间消费
      if ($$.buyWay.saleType !== 1) message.success('外设入账成功');
      // 关闭抽屉 并刷新上一页面
      handleClose();
    }
  };

  // 收款
  const gotoPay = async (confirm?: boolean) => {
    // 付款单需confirm后下单
    if (!confirm && payType === PayWay.Fukuandan) {
      payModalRef.current.openModal({
        payType,
        showFukuanDanConfirm: true,
      });
      return;
    }

    setLoading(true);
    try {
      // 获取支付单号
      const res: any = await orderBillPay({
        fkdFlag: payType === PayWay.Fukuandan,
        orderNo,
        payPrice: $$.actualPay,
        payType: 103,
        remark: $$.financialNotes,
      });
      if (res.code === 0) {
        payModalRef.current.openModal({
          payAmount: $$.actualPay,
          payType,
          payOrderType: 103,
          paymentNo: res.data,
          certNo,
          wyuid,
          businessType: $$.buyWay.saleType === 1 ? 4 : 1,
          // 商品前台直售，没有付款单
          // businessType: 4,
          // 商品直售不用传
          extraParams:
            $$.buyWay.saleType === 1
              ? {}
              : { trigger: '挂账房间方式，且入账成功时', operationPage: '外设租借结算抽屉' },
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // 支付类型选择
  const payRadioChange = (type: 0 | 1) => {
    setPaymentTypes(type);
  };

  // 改变租借次数 失去焦点  $$.orderDetail
  const changeRentCountBlur = async (item: any, count: number) => {
    updateModalState({ isLoading: true });
    try {
      const details = ($$.settleGoodsList || []).map((obj: any) => {
        if (obj.id === item.id) {
          return {
            goodsPrice: obj.goodsPrice,
            saleCount: obj.goodsCount,
            saleTimeCount: count,
            skuCode: obj.skuCode,
          };
        }
        return {
          goodsPrice: obj.goodsPrice,
          saleCount: obj.goodsCount,
          saleTimeCount: obj.goodsTimeCount,
          skuCode: obj.skuCode,
        };
      });

      await preOrder({
        details,
        leaseType, // 1-正常租  2-续租
        orderNo,
        recNo: $$.orderDetail?.recNo,
        resvNo: $$.orderDetail?.resvNo,
        roomId: $$.orderDetail?.roomId,
        roomNo: $$.orderDetail?.roomNo,
        saleType: 0,
      });

      $$.getOrderInfos(orderNo);
    } catch (error) {
      //
    }
    updateModalState({ isLoading: false });
  };

  if (!modalState.isInit) return <PortalLoading />;

  return (
    <Spin spinning={modalState.isLoading}>
      <DrawerHeader title="外设租借结算" onClose={$$.unmountNode} />
      <section className={styles.container}>
        <div className={styles.GoodsSettle}>
          <div className={styles.main}>
            <div className={styles.left}>
              <p className={styles.title}>租借列表</p>

              <RentList
                list={$$.settleGoodsList}
                changeRentCount={$$.changeRentCount}
                changeRentCountBlur={changeRentCountBlur}
              />
              <TotalInfo
                orderNo={orderNo}
                goodTotalInfo={goodTotalInfo}
                orderChangePrice={orderChangePrice}
                getOrderInfos={$$.getOrderInfos}
              />
            </div>
            <div className={styles.right}>
              <div className={styles.totalPee}>
                <p className={styles.title}>外设租借结算</p>
              </div>
              <div className={styles.payWay}>
                <div className={styles.payItem}>
                  {intl.get('goods_sale_way_colon')}
                  {$$.buyWay.saleType === 1
                    ? intl.get('goods_font_direct_sale')
                    : intl.get('goods_bill_room')}
                </div>
                {$$.buyWay.saleType !== 1 && (
                  <div className={styles.payItem}>
                    {intl.get('goods_enter_bill_room_colon')}
                    {homeowner?.roomNo} {homeowner?.name} {homeowner?.certNo}
                  </div>
                )}
                <div className={styles.payWayAmout}>
                  {intl.get('goods_accounts_receivable_colon')}
                  {parseFen2Currency($$.actualPay || 0)}
                </div>
                <div className={styles.payItem}>
                  {intl.get('goods_pay_type_colon')}
                  <Radio.Group
                    name="paymentTypes"
                    onChange={(e) => payRadioChange(e.target.value)}
                    value={paymentTypes}
                  >
                    <Radio value={0}>{intl.get('goods_account_entry_direct_payment')}</Radio>
                    {$$.buyWay.saleType !== 1 && (
                      <Radio value={1}>{intl.get('goods_entry_account_settle_later')}</Radio>
                    )}
                  </Radio.Group>
                </div>
                {paymentTypes === 0 && (
                  <Payment
                    isInitPayTypes
                    selectPayType={(type) => selectPayType(type)}
                    // BusinessType = 1 | 2 | 3 | 4; // 收款业务类型, 1: 房间消费, 2: 购买会员卡, 3: 会员充值, 4: 前台直售
                    businessType={$$.buyWay.saleType === 1 ? 4 : 1}
                    // businessType={4}
                    // resvNo={$$.buyWay?.resvNo}
                    resvNo={homeowner?.resvNo}
                    setIdCardNo={(no) => setCertNo(no)}
                    setWyuid={(uid) => setWyuid(uid)}
                  />
                )}
                <Form.Item
                  label={intl.get('goods_bill_remarks_colon')}
                  style={{ marginTop: '16px' }}
                >
                  <TextArea
                    placeholder={intl.get('app_search_form_placeholder_prefix')}
                    maxLength={10000}
                    value={$$.financialNotes}
                    onChange={(e) => {
                      $$.updateState({ financialNotes: e.target.value });
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </div>
        <PayModal
          ref={payModalRef}
          payResultCallback={(payResult: any) => payResultCallback(payResult)}
          onConfirmOk={() => gotoPay(true)}
        />
      </section>
      <DrawerFooter>
        <Button
          type="primary"
          onClick={() => {
            // eslint-disable-next-line no-unused-expressions
            paymentTypes === 0 ? gotoPay() : noPayIntoOrder();
          }}
        >
          确定
        </Button>
        <Button onClick={$$.unmountNode}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
    </Spin>
  );
};

const mapStateToProps = (state: any) => {
  return {
    ...state.peripheral,
  };
};

export default () =>
  usePortalDrawer<IConfig>(
    connect(mapStateToProps, { getOrderInfos, updateState, changeRentCount })(React.memo(Index)),
  );
